import { useState, useMemo, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  entityTypeConfig,
  badgeConfig,
  verificationStats,
  verifiedEntities,
  verificationBenefits,
  verificationRules,
  getVerifiedByType,
  getVerifiedByBadge,
  type VerificationEntityType,
  type VerificationBadgeLevel,
  type VerifiedEntity,
} from '@/mocks/rfwVerification';

type PageState = 'loading' | 'error' | 'ready';
type ViewSection = 'verificados' | 'insignias' | 'beneficios' | 'reglas';

function EntityBadge({ type }: { type: VerificationEntityType }) {
  const c = entityTypeConfig[type];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-semibold ${c.bgColor} ${c.color} border border-current/20`}>
      <i className={`${c.icon} text-[8px]`}></i>
      {c.label}
    </span>
  );
}

function LevelBadge({ level }: { level: VerificationBadgeLevel }) {
  const c = badgeConfig[level];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-semibold ${c.bgColor} ${c.color} border border-current/20`}>
      <i className={`${c.icon} text-[8px]`}></i>
      {c.label}
    </span>
  );
}

export default function VerificacionPage() {
  const [pageState, setPageState] = useState<PageState>('loading');
  const [activeView, setActiveView] = useState<ViewSection>('verificados');
  const [typeFilter, setTypeFilter] = useState<VerificationEntityType | 'todas'>('todas');
  const [levelFilter, setLevelFilter] = useState<VerificationBadgeLevel | 'todas'>('todas');
  const [selectedEntity, setSelectedEntity] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<'recent' | 'followers' | 'rating'>('recent');

  useEffect(() => {
    const t = setTimeout(() => setPageState('ready'), 400);
    return () => clearTimeout(t);
  }, []);

  const handleRetry = useCallback(() => {
    setPageState('loading');
    setTimeout(() => setPageState('ready'), 400);
  }, []);

  const filtered = useMemo(() => {
    let result = getVerifiedByType(typeFilter);
    if (levelFilter !== 'todas') result = result.filter((e) => e.badgeLevel === levelFilter);
    if (sortBy === 'followers') result = [...result].sort((a, b) => b.stats.followers - a.stats.followers);
    else if (sortBy === 'rating') result = [...result].sort((a, b) => b.stats.rating - a.stats.rating);
    return result;
  }, [typeFilter, levelFilter, sortBy]);

  const premium = getVerifiedByBadge('premium');
  const oficiales = getVerifiedByBadge('oficial');

  const views: { id: ViewSection; label: string; icon: string; badge?: number }[] = [
    { id: 'verificados', label: 'Verificados', icon: 'ri-verified-badge-line', badge: verifiedEntities.length },
    { id: 'insignias', label: 'Niveles de Insignia', icon: 'ri-shield-star-line', badge: 3 },
    { id: 'beneficios', label: 'Beneficios', icon: 'ri-star-line', badge: 3 },
    { id: 'reglas', label: 'Reglas', icon: 'ri-file-list-3-line', badge: 3 },
  ];

  if (pageState === 'loading') {
    return (
      <div className="min-h-screen bg-background-50">
        <section className="relative w-full h-[200px] md:h-[260px] bg-background-200 animate-pulse" />
        <main className="max-w-[1280px] mx-auto px-4 md:px-6 py-8 space-y-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-1 bg-background-100 rounded-2xl h-[600px] animate-pulse" />
            <div className="md:col-span-2 bg-background-100 rounded-2xl h-[600px] animate-pulse" />
          </div>
        </main>
      </div>
    );
  }

  if (pageState === 'error') {
    return (
      <div className="min-h-screen bg-background-50 flex flex-col items-center justify-center px-4 py-20">
        <div className="w-20 h-20 rounded-3xl bg-red-100 flex items-center justify-center mb-5">
          <i className="ri-error-warning-line text-3xl text-red-500"></i>
        </div>
        <h2 className="text-xl font-extrabold text-foreground-950 mb-2">Error al cargar verificaciones</h2>
        <p className="text-sm text-foreground-500 mb-6 text-center max-w-md">No pudimos cargar los datos del sistema de verificación. Verificá tu conexión e intentá de nuevo.</p>
        <button onClick={handleRetry} className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-xl text-sm font-bold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
          <i className="ri-refresh-line"></i>Reintentar
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      {/* HERO */}
      <section className="relative w-full h-[220px] md:h-[320px] overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Professional%20verification%20badge%20concept%20abstract%20golden%20shield%20emblem%20floating%20translucent%20geometric%20patterns%20trust%20authenticity%20theme%20warm%20emerald%20amber%20tones%20editorial%20product%20photography%20clean%20modern%20aesthetic%20soft%20shadows&width=1600&height=900&seq=rfw-verificacion-hero&orientation=landscape"
          alt="Sistema de Verificación RFW"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/70"></div>
        <div className="absolute inset-0 flex flex-col items-center justify-center px-4 text-center">
          <div className="w-16 h-16 rounded-2xl bg-background-50/15 backdrop-blur-md flex items-center justify-center mb-4 border border-background-50/20">
            <i className="ri-verified-badge-line text-3xl text-background-50"></i>
          </div>
          <h1 className="text-3xl md:text-5xl font-extrabold text-background-50 leading-tight mb-3">
            Sistema de Verificación
          </h1>
          <p className="text-sm md:text-base text-background-50/80 max-w-xl leading-relaxed">
            El sello de confianza RFW. Cinco tipos de entidad, tres niveles de insignia. Más confianza, más visibilidad, mayor prioridad. Solo SA puede verificar.
          </p>
        </div>
      </section>

      {/* MAIN */}
      <main className="max-w-[1280px] mx-auto px-4 md:px-6 py-6 md:py-8">
        {/* LIVE PULSE BAR */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3 mb-6">
          {[
            { label: 'Total Verificados', value: (verificationStats.totalVerificados / 1000).toFixed(1) + 'K', icon: 'ri-verified-badge-line', color: 'text-emerald-600 bg-emerald-50' },
            { label: 'Radios', value: verificationStats.porRadio.toString(), icon: 'ri-radio-line', color: 'text-emerald-600 bg-emerald-50' },
            { label: 'Programas', value: verificationStats.porPrograma.toString(), icon: 'ri-calendar-check-line', color: 'text-sky-600 bg-sky-50' },
            { label: 'Locutores', value: verificationStats.porLocutor.toString(), icon: 'ri-mic-line', color: 'text-rose-600 bg-rose-50' },
            { label: 'Pendientes', value: verificationStats.pendientes.toString(), icon: 'ri-hourglass-line', color: 'text-amber-600 bg-amber-50' },
            { label: 'Hoy', value: verificationStats.verificacionesHoy.toString(), icon: 'ri-calendar-line', color: 'text-violet-600 bg-violet-50' },
          ].map((stat, i) => (
            <div key={i} className="flex items-center gap-2.5 p-3 rounded-xl bg-background-50 border border-background-200/70">
              <div className={`w-8 h-8 rounded-full ${stat.color} flex items-center justify-center shrink-0`}>
                <i className={`${stat.icon} text-xs`}></i>
              </div>
              <div>
                <p className="text-sm font-extrabold text-foreground-950">{stat.value}</p>
                <p className="text-[9px] text-foreground-500">{stat.label}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6">
          {/* SIDEBAR */}
          <aside className="md:col-span-3">
            <div className="rounded-2xl bg-background-50 border border-background-200/70 overflow-hidden sticky top-4">
              <div className="p-4 border-b border-background-200/50">
                <h2 className="text-sm font-extrabold text-foreground-950 mb-1">Sistema de Verificación</h2>
                <p className="text-[10px] text-foreground-500">El sello de confianza RFW</p>
              </div>

              <nav className="p-2">
                {views.map((v) => (
                  <button
                    key={v.id}
                    onClick={() => setActiveView(v.id)}
                    className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-left transition-all duration-200 cursor-pointer mb-1 ${
                      activeView === v.id
                        ? 'bg-emerald-50 text-emerald-700 font-bold'
                        : 'text-foreground-600 hover:bg-background-100'
                    }`}
                  >
                    <i className={`${v.icon} text-sm ${activeView === v.id ? 'text-emerald-600' : ''}`}></i>
                    <span className="text-xs flex-1">{v.label}</span>
                    {v.badge !== undefined && v.badge > 0 && (
                      <span className={`w-5 h-5 rounded-full text-[9px] font-bold flex items-center justify-center ${
                        activeView === v.id ? 'bg-emerald-200 text-emerald-700' : 'bg-background-100 text-foreground-500'
                      }`}>
                        {v.badge}
                      </span>
                    )}
                  </button>
                ))}
              </nav>

              <div className="p-3 mx-2 mb-2 rounded-xl bg-background-100">
                <p className="text-[10px] font-bold text-foreground-600 mb-2">Tipos de Entidad</p>
                <div className="space-y-1">
                  {Object.entries(entityTypeConfig).map(([key, c]) => (
                    <div key={key} className="flex items-center gap-2 text-[10px]">
                      <div className={`w-5 h-5 rounded-full ${c.bgColor} flex items-center justify-center shrink-0`}>
                        <i className={`${c.icon} text-[9px] ${c.color}`}></i>
                      </div>
                      <span className="text-foreground-600">{c.labelPlural}</span>
                      <span className="ml-auto text-foreground-400 font-mono">{verifiedEntities.filter((e) => e.entityType === key).length}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </aside>

          {/* MAIN CONTENT */}
          <div className="md:col-span-9">
            {/* VIEW: VERIFICADOS */}
            {activeView === 'verificados' && (
              <div className="space-y-5">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex flex-wrap items-center gap-1.5">
                    {([
                      { id: 'todas' as const, label: 'Todas', icon: 'ri-list-check-3' },
                      ...Object.entries(entityTypeConfig).map(([key, c]) => ({ id: key as VerificationEntityType, label: c.labelPlural, icon: c.icon })),
                    ]).map((f) => (
                      <button
                        key={f.id}
                        onClick={() => setTypeFilter(f.id)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-medium transition-all duration-200 whitespace-nowrap cursor-pointer border ${
                          typeFilter === f.id
                            ? 'bg-primary-500 text-white border-primary-500'
                            : 'bg-background-50 text-foreground-500 border-background-200 hover:border-foreground-300'
                        }`}
                      >
                        <i className={`${f.icon} text-[9px]`}></i>
                        {f.label}
                      </button>
                    ))}
                  </div>

                  <div className="flex items-center gap-1.5">
                    {([
                      { id: 'recent' as const, label: 'Recientes', icon: 'ri-time-line' },
                      { id: 'followers' as const, label: 'Más Seguidos', icon: 'ri-group-line' },
                      { id: 'rating' as const, label: 'Mejor Rating', icon: 'ri-star-line' },
                    ]).map((s) => (
                      <button
                        key={s.id}
                        onClick={() => setSortBy(s.id)}
                        className={`flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[10px] font-medium transition-all duration-200 whitespace-nowrap cursor-pointer border ${
                          sortBy === s.id
                            ? 'bg-emerald-500 text-white border-emerald-500'
                            : 'bg-background-50 text-foreground-500 border-background-200 hover:border-emerald-300'
                        }`}
                      >
                        <i className={`${s.icon} text-[9px]`}></i>
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-1.5">
                  {([
                    { id: 'todas' as const, label: 'Todos los Niveles', icon: 'ri-list-check-3' },
                    ...Object.entries(badgeConfig).map(([key, c]) => ({ id: key as VerificationBadgeLevel, label: c.label, icon: c.icon })),
                  ]).map((f) => (
                    <button
                      key={f.id}
                      onClick={() => setLevelFilter(f.id)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-medium transition-all duration-200 whitespace-nowrap cursor-pointer border ${
                        levelFilter === f.id
                          ? 'bg-primary-500 text-white border-primary-500'
                          : 'bg-background-50 text-foreground-500 border-background-200 hover:border-foreground-300'
                      }`}
                    >
                      <i className={`${f.icon} text-[9px]`}></i>
                      {f.label}
                    </button>
                  ))}
                </div>

                <p className="text-[10px] text-foreground-400">
                  {filtered.length} entidad{filtered.length !== 1 ? 'es' : ''} verificada{filtered.length !== 1 ? 's' : ''}
                </p>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                  {filtered.map((entity) => (
                    <EntityCard
                      key={entity.id}
                      entity={entity}
                      isSelected={selectedEntity === entity.id}
                      onClick={() => setSelectedEntity(selectedEntity === entity.id ? null : entity.id)}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* VIEW: INSIGNIAS */}
            {activeView === 'insignias' && (
              <div className="space-y-5">
                <div className="grid grid-cols-1 gap-5">
                  {Object.entries(badgeConfig).sort(([, a], [, b]) => b.priority - a.priority).map(([key, c]) => {
                    const entities = getVerifiedByBadge(key as VerificationBadgeLevel);
                    return (
                      <div key={key} className={`p-5 rounded-2xl border-2 ${c.borderColor} bg-background-50`}>
                        <div className="flex items-start gap-4 mb-4">
                          <div className={`w-16 h-16 rounded-2xl ${c.bgColor} flex items-center justify-center shrink-0`}>
                            <i className={`${c.iconBadge} text-4xl ${c.color}`}></i>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="text-lg font-extrabold text-foreground-950">{c.label}</h3>
                              <span className={`text-xs font-bold ${c.color} bg-background-100 px-2 py-0.5 rounded-full`}>
                                Nivel {c.priority}
                              </span>
                            </div>
                            <p className="text-xs text-foreground-500 leading-relaxed">{c.description}</p>
                          </div>
                          <div className="text-center shrink-0">
                            <p className="text-2xl font-extrabold text-foreground-950">{entities.length}</p>
                            <p className="text-[9px] text-foreground-400">entidades</p>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                          {entities.map((e) => (
                            <div key={e.id} className="flex items-center gap-2 p-2 rounded-lg bg-background-100">
                              <img src={e.entityPhotoUrl} alt={e.entityName} className="w-8 h-8 rounded-full object-cover shrink-0" />
                              <div className="min-w-0">
                                <p className="text-[10px] font-bold text-foreground-950 line-clamp-1">{e.entityName}</p>
                                <p className="text-[8px] text-foreground-400">{entityTypeConfig[e.entityType].label}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* VIEW: BENEFICIOS */}
            {activeView === 'beneficios' && (
              <div className="space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  {verificationBenefits.map((benefit) => (
                    <div key={benefit.id} className={`p-5 rounded-2xl ${benefit.bgColor} border border-background-200/50`}>
                      <div className="w-14 h-14 rounded-2xl bg-background-50 flex items-center justify-center mb-4">
                        <i className={`${benefit.icon} text-2xl ${benefit.color}`}></i>
                      </div>
                      <p className="text-base font-bold text-foreground-950 mb-2">{benefit.title}</p>
                      <p className="text-[11px] text-foreground-500 mb-4 leading-relaxed">{benefit.description}</p>
                      <ul className="space-y-2">
                        {benefit.bulletPoints.map((bp, i) => (
                          <li key={i} className="text-[10px] text-foreground-600 flex items-start gap-2">
                            <i className={`${benefit.color} text-[10px] ri-check-double-line mt-0.5 shrink-0`}></i>
                            {bp}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>

                <div className="p-5 rounded-2xl bg-background-50 border border-background-200/70">
                  <h3 className="text-sm font-extrabold text-foreground-950 mb-3 flex items-center gap-2">
                    <i className="ri-bar-chart-line text-emerald-500"></i>
                    Impacto de la Verificación
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {[
                      { label: 'Más Visibilidad', value: '+340%', sub: 'en resultados de búsqueda', icon: 'ri-search-eye-line', color: 'text-sky-600 bg-sky-50' },
                      { label: 'Más Confianza', value: '+215%', sub: 'en nuevos seguidores', icon: 'ri-user-add-line', color: 'text-emerald-600 bg-emerald-50' },
                      { label: 'Mayor Interacción', value: '+178%', sub: 'en comentarios y reacciones', icon: 'ri-heart-line', color: 'text-rose-600 bg-rose-50' },
                      { label: 'Menos Reportes', value: '-92%', sub: 'en suplantación', icon: 'ri-shield-check-line', color: 'text-amber-600 bg-amber-50' },
                    ].map((stat, i) => (
                      <div key={i} className="p-3 rounded-xl bg-background-100 text-center">
                        <div className={`w-8 h-8 rounded-full ${stat.color} flex items-center justify-center mx-auto mb-1.5`}>
                          <i className={`${stat.icon} text-xs`}></i>
                        </div>
                        <p className="text-lg font-extrabold text-foreground-950">{stat.value}</p>
                        <p className="text-[9px] text-foreground-500">{stat.sub}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* VIEW: REGLAS */}
            {activeView === 'reglas' && (
              <div className="space-y-5">
                {verificationRules.map((rule, i) => (
                  <div key={rule.id} className={`p-5 rounded-2xl ${rule.bgColor} border border-background-200/50`}>
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-full bg-background-50 flex items-center justify-center shrink-0 mt-1">
                        <i className={`${rule.icon} text-xl ${rule.color}`}></i>
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-[10px] font-bold text-foreground-400 uppercase tracking-wider">Regla #{i + 1}</span>
                        </div>
                        <h3 className="text-base font-extrabold text-foreground-950 mb-2">{rule.title}</h3>
                        <p className="text-xs text-foreground-600 leading-relaxed">{rule.description}</p>
                      </div>
                    </div>
                  </div>
                ))}

                <div className="p-5 rounded-2xl bg-background-50 border border-background-200/70">
                  <h3 className="text-sm font-extrabold text-foreground-950 mb-3 flex items-center gap-2">
                    <i className="ri-git-repository-line text-violet-500"></i>
                    Trazabilidad de Verificación
                  </h3>
                  <p className="text-[11px] text-foreground-500 mb-3">
                    Cada verificación deja un registro inmutable con: quién solicitó, quién revisó, quién otorgó y cuándo. Este es un ejemplo del audit trail de una entidad verificada:
                  </p>
                  <div className="bg-background-100 rounded-xl p-4 font-mono text-[10px] text-foreground-600 space-y-1">
                    {verifiedEntities[0] && (
                      <>
                        <p><span className="text-foreground-400">Solicitante:</span> {verifiedEntities[0].auditTrail.requestedBy}</p>
                        <p><span className="text-foreground-400">Solicitud:</span> {verifiedEntities[0].auditTrail.requestedAt}</p>
                        <p><span className="text-foreground-400">Revisor:</span> {verifiedEntities[0].auditTrail.reviewedBy}</p>
                        <p><span className="text-foreground-400">Revisión:</span> {verifiedEntities[0].auditTrail.reviewedAt}</p>
                        <p><span className="text-foreground-400">Otorgante:</span> {verifiedEntities[0].auditTrail.grantedBy}</p>
                        <p><span className="text-foreground-400">Otorgado:</span> {verifiedEntities[0].auditTrail.grantedAt}</p>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* CROSS LINKS */}
        <section className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-10 mb-6">
          {[
            { to: '/moderadores', icon: 'ri-shield-user-line', iconBg: 'bg-rose-100', iconColor: 'text-rose-600', label: 'Moderadores', desc: 'Quiénes protegen la comunidad' },
            { to: '/reportes', icon: 'ri-flag-line', iconBg: 'bg-amber-100', iconColor: 'text-amber-600', label: 'Reportes', desc: 'Denunciá suplantaciones' },
            { to: '/rankings', icon: 'ri-trophy-line', iconBg: 'bg-amber-100', iconColor: 'text-amber-600', label: 'Rankings', desc: 'Los mejores de RFW' },
            { to: '/centro-social', icon: 'ri-pulse-line', iconBg: 'bg-sky-100', iconColor: 'text-sky-600', label: 'Centro Social', desc: 'Tendencias de la comunidad' },
          ].map((link, i) => (
            <Link key={i} to={link.to} className="p-4 rounded-xl bg-background-100 border border-background-200/70 hover:border-emerald-300/60 transition-all duration-200 text-center">
              <div className={`w-10 h-10 rounded-full ${link.iconBg} flex items-center justify-center mx-auto mb-2`}>
                <i className={`${link.icon} text-base ${link.iconColor}`}></i>
              </div>
              <p className="text-sm font-semibold text-foreground-900">{link.label}</p>
              <p className="text-[10px] text-foreground-500 mt-1">{link.desc}</p>
            </Link>
          ))}
        </section>

        {/* REGLA FINAL */}
        <section className="p-6 rounded-2xl bg-violet-100/70 border border-violet-200/50 text-center">
          <div className="w-12 h-12 rounded-full bg-primary-500 flex items-center justify-center mx-auto mb-3">
            <i className="ri-admin-line text-xl text-white"></i>
          </div>
          <p className="text-lg font-extrabold text-foreground-950 mb-2">Solo SA puede verificar.</p>
          <p className="text-sm text-foreground-600 max-w-2xl mx-auto">
            En RFW la confianza no es un botón — es un proceso. Solo los Super Admins tienen la autoridad de otorgar, modificar o revocar una insignia de verificación. No se compra, no se hereda, no se transfiere. Cada insignia es una decisión informada basada en mérito, trayectoria demostrada y calidad comprobable. Radios, programas, locutores, patrocinadores y empresas pasan por el mismo proceso riguroso. Porque cuando ves una insignia de verificación en RFW, sabés que detrás hay una entidad real, confiable y merecedora de ese lugar. La credibilidad de nuestra comunidad depende de este estándar — y no lo delegamos a algoritmos ni a automatizaciones.
          </p>
        </section>
      </main>
    </div>
  );
}

function EntityCard({ entity, isSelected, onClick }: { entity: VerifiedEntity; isSelected: boolean; onClick: () => void }) {
  const tc = entityTypeConfig[entity.entityType];
  const bc = badgeConfig[entity.badgeLevel];

  return (
    <div
      className={`p-4 rounded-xl border transition-all duration-200 cursor-pointer ${
        isSelected
          ? 'bg-emerald-50 border-emerald-300/60 shadow-sm'
          : 'bg-background-50 border-background-200/70 hover:border-emerald-300/60'
      }`}
      onClick={onClick}
    >
      <div className="flex items-start gap-3 mb-2">
        <div className="relative shrink-0">
          <img src={entity.entityPhotoUrl} alt={entity.entityName} className="w-12 h-12 rounded-xl object-cover" />
          <div className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full ${bc.bgColor} flex items-center justify-center border-2 border-background-50`}>
            <i className={`${bc.iconBadge} text-[9px] ${bc.color}`}></i>
          </div>
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 mb-0.5">
            <p className="text-xs font-bold text-foreground-950">{entity.entityName}</p>
            <EntityBadge type={entity.entityType} />
            <LevelBadge level={entity.badgeLevel} />
            <span className="text-[10px] text-foreground-400 ml-auto shrink-0">{entity.verifiedAtRelative}</span>
          </div>
          <p className="text-[10px] text-foreground-500">{entity.category}</p>

          {isSelected && (
            <div className="mt-3 pt-3 border-t border-background-200/50 animate-fadeIn space-y-2">
              <p className="text-[11px] text-foreground-700">{entity.description}</p>

              <div className="grid grid-cols-3 gap-2">
                <div className="p-2 rounded-lg bg-background-100 text-center">
                  <p className="text-xs font-extrabold text-foreground-950">{(entity.stats.followers / 1000).toFixed(0)}K</p>
                  <p className="text-[8px] text-foreground-400">Seguidores</p>
                </div>
                <div className="p-2 rounded-lg bg-background-100 text-center">
                  <p className="text-xs font-extrabold text-foreground-950">{entity.stats.rating}</p>
                  <p className="text-[8px] text-foreground-400">Rating</p>
                </div>
                <div className="p-2 rounded-lg bg-background-100 text-center">
                  <p className="text-xs font-extrabold text-foreground-950">{(entity.stats.interactions / 1000000).toFixed(1)}M</p>
                  <p className="text-[8px] text-foreground-400">Interacciones</p>
                </div>
              </div>

              {entity.location && (
                <p className="text-[10px] text-foreground-500 flex items-center gap-1">
                  <i className="ri-map-pin-line text-[9px]"></i>
                  {entity.location}
                  {entity.audience && <span className="text-foreground-300">· {entity.audience}</span>}
                </p>
              )}

              <div className="flex flex-wrap gap-1">
                {entity.benefits.slice(0, 3).map((b, i) => (
                  <span key={i} className={`text-[8px] px-2 py-0.5 rounded-full ${bc.bgColor} ${bc.color} border border-current/10`}>
                    {b}
                  </span>
                ))}
              </div>

              <div className="text-[9px] text-foreground-400 pt-1">
                <span className="font-semibold text-foreground-500">Verificado por:</span> {entity.verifiedBy} ({entity.verifiedByRole})
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}